import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { InventoryPageHintComponent } from './inventory-page-hint.component';
import { bindInventoryLiveUpdates } from './inventory-live-updates';
import { LiveUpdateService } from '../../core/live-updates/live-update.service';
import { CurrentContextService } from '../../core/context/current-context.service';
import { CatalogOffering, CatalogService, CatalogWarehouse, InventoryReplenishmentBreachItem } from './catalog.service';

@Component({
  standalone: true,
  selector: 'ain-inventory-replenishment-breaches',
  imports: [AinPageComponent, AinStateComponent, InventoryPageHintComponent, RouterLink, FormsModule, SelectModule, TableModule],
  template: `
    <ain-page [title]="t('catalog.replenishment.breachesTitle')" [description]="t('catalog.replenishment.breachesDescription')">
      <ain-inventory-page-hint pageKey="replenishmentBreaches" />
      @if (loading()) {
        <ain-state type="loading" [title]="t('catalog.loading')" />
      } @else {
        <section class="filters">
          <a routerLink="/inventory/replenishment-policies">{{ t('catalog.replenishment.backToPolicies') }}</a>
          <label><span>{{ t('catalog.inventory.warehouseId') }}</span>
            <p-select [(ngModel)]="warehouseFilter" [options]="warehouseOptions()" optionLabel="label" optionValue="value" showClear="true" appendTo="body" (ngModelChange)="load()" />
          </label>
          <label><span>{{ t('catalog.replenishment.breachCategory') }}</span>
            <p-select [(ngModel)]="categoryFilter" [options]="categoryOptions" optionLabel="label" optionValue="value" showClear="true" appendTo="body" (ngModelChange)="load()" />
          </label>
        </section>
        <p-table [value]="items()" [paginator]="true" [rows]="20">
          <ng-template pTemplate="header">
            <tr>
              <th>{{ t('catalog.replenishment.breachCategory') }}</th>
              <th>{{ t('catalog.inventory.offeringId') }}</th>
              <th>{{ t('catalog.replenishment.onHand') }}</th>
              <th>{{ t('catalog.replenishment.reserved') }}</th>
              <th>{{ t('catalog.replenishment.available') }}</th>
              <th>{{ t('catalog.replenishment.policyId') }}</th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-row>
            <tr>
              <td>{{ categoryLabel(row.category) }}</td>
              <td>{{ offeringLabel(row.offeringId) }}</td>
              <td>{{ row.onHandQuantity }}</td>
              <td>{{ row.reservedQuantity }}</td>
              <td>{{ row.availableQuantity }}</td>
              <td><a [routerLink]="['/inventory/replenishment-policies', row.policyId]">{{ t('common.open') }}</a></td>
            </tr>
          </ng-template>
        </p-table>
        @if (error()) { <p class="error">{{ error() }}</p> }
      }
    </ain-page>
  `,
  styles: [`
    .filters { display: flex; flex-wrap: wrap; gap: 12px; align-items: end; margin-bottom: 12px; }
    .filters label { display: grid; gap: 6px; min-width: 200px; }
    .error { color: #b91c1c; }
  `]
})
export class InventoryReplenishmentBreachesComponent implements OnInit {
  private readonly service = inject(CatalogService);
  private readonly localization = inject(LocalizationService);
  private readonly liveUpdateService = inject(LiveUpdateService);
  private readonly contextService = inject(CurrentContextService);
  private readonly destroyRef = inject(DestroyRef);

  readonly loading = signal(true);
  readonly error = signal('');
  readonly items = signal<InventoryReplenishmentBreachItem[]>([]);
  readonly offerings = signal<CatalogOffering[]>([]);
  readonly warehouses = signal<CatalogWarehouse[]>([]);
  warehouseFilter: string | null = null;
  categoryFilter: string | null = null;

  get categoryOptions() {
    return [
      { label: this.t('catalog.replenishment.breachCategory.belowMinimum'), value: 'BelowMinimum' },
      { label: this.t('catalog.replenishment.breachCategory.atOrBelowReorder'), value: 'AtOrBelowReorder' },
      { label: this.t('catalog.replenishment.breachCategory.aboveMaximum'), value: 'AboveMaximum' }
    ];
  }

  warehouseOptions = () => this.warehouses().map(w => ({ label: `${w.code} — ${w.name}`, value: w.id }));
  offeringLabel = (id: string) => {
    const o = this.offerings().find(x => x.id === id);
    return o ? `${o.code} — ${o.title}` : id;
  };
  categoryLabel = (category: string) => {
    const key = `catalog.replenishment.breachCategory.${category.charAt(0).toLowerCase()}${category.slice(1)}`;
    const translated = this.t(key);
    return translated === key ? category : translated;
  };

  t(key: string): string { return this.localization.translate(key); }

  ngOnInit(): void {
    bindInventoryLiveUpdates(
      { liveUpdateService: this.liveUpdateService, contextService: this.contextService, destroyRef: this.destroyRef },
      () => void this.load(false)
    );
    this.service.listOfferings().subscribe({ next: o => this.offerings.set(o) });
    this.service.listWarehouses().subscribe({ next: w => this.warehouses.set(w) });
    void this.load();
  }

  load(showLoading = true): void {
    if (showLoading) this.loading.set(true);
    this.service.listInventoryReplenishmentBreaches(this.warehouseFilter ?? undefined, this.categoryFilter ?? undefined).subscribe({
      next: r => { this.items.set(r.items); this.loading.set(false); },
      error: () => { this.error.set(this.t('catalog.replenishment.breachesLoadFailed')); this.loading.set(false); }
    });
  }
}
